import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';

import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { routes } from '../../routes/app';
import { useAuth } from '../../contexts/AuthContext';
import SuspenseOutlet from '../SuspenseOutlet/SuspenseOutlet';
import { useRevaShellOffset } from '../../hooks/useRevaPlacement';
import useNavExpanded from '../../hooks/useNavExpanded';
import {
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useEffect } from 'react';
import { getEnv } from '../../utils/env';
import ContactDialog from '../ContactDialog';
import NavRail from './NavRail';
import AppHeader, { APP_HEADER_HEIGHT } from './AppHeader';
import AccountMenu from './AccountMenu';
import {
  NAV_WIDTH_COLLAPSED,
  NAV_WIDTH_EXPANDED,
  NAV_WIDTH_MOBILE,
  headerTitleFor,
  initialsFor,
} from './navModel';
import useNavNarrowing from './useNavNarrowing';
import { widenSignupIntentToBoth } from '../../pages/HowItWorks/howItWorksStorage';
import { PageTitleContext } from '../../contexts/PageTitleContext';

const BROWSE_TAGLINE = 'Turn the agent fee into buying power';

/**
 * The signed-in app shell: the navy nav rail on the left (a sheet on phones),
 * the white page header, and the routed page. Signed-out visitors to public
 * routes get the same white header (logo + "How it works") with no rail.
 */
const MiniDrawer: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Map-centric pages escape the lg container: the map should use every pixel
  const fullBleedRoute = ['/admin/command-center', '/lakes'].includes(
    location.pathname
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [expanded, toggleExpanded] = useNavExpanded();
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);
  const [accountAnchor, setAccountAnchor] = React.useState<HTMLElement | null>(
    null
  );
  const { isAuthenticated, logout } = useAuth();
  const authLoading = useSelector((state: RootState) => state.auth.loading);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { hiddenKeys, widenLabel } = useNavNarrowing();
  const routeTitle = headerTitleFor(routes, location.pathname, matchPath);
  const [pageTitle, setPageTitle] = React.useState(routeTitle);

  useEffect(() => {
    setMobileOpen(false);
    setAccountAnchor(null);
    setPageTitle(routeTitle);
  }, [location, routeTitle]);

  const hidden = !isAuthenticated;
  // Cold ad traffic lands on /browse; say what Homepoint is before the listings
  const showTagline = location.pathname === '/browse';
  // Space the docked Reva column (or its collapsed rail) claims on the right.
  const revaOffset = useRevaShellOffset();
  const navWidth = (() => {
    if (hidden || isMobile) return 0;
    return expanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED;
  })();

  const widthTransition = theme.transitions.create(['width', 'padding-right'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  const fullName =
    [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') ||
    currentUser?.email ||
    'Account';

  return (
    <PageTitleContext.Provider value={setPageTitle}>
    <Box
      sx={{
        display: 'flex',
        backgroundColor: '#f6f9fb',
        minHeight: '100vh',
        paddingBottom: theme.spacing(4),
        paddingRight: `${revaOffset}px`,
        transition: widthTransition,
        // Fixed-position pages (the /browse map) offset themselves by the rail's live width
        '--app-nav-width': `${navWidth}px`,
      }}
    >
      <CssBaseline />
      {!hidden && (
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          data-testid="app-drawer"
          sx={{
            width: navWidth,
            flexShrink: 0,
            transition: widthTransition,
            '& .MuiDrawer-paper': {
              width: isMobile ? NAV_WIDTH_MOBILE : navWidth,
              maxWidth: '86vw',
              boxSizing: 'border-box',
              border: 0,
              overflowX: 'hidden',
              backgroundColor: theme.palette.secondary.main,
              color: '#fff',
              transition: widthTransition,
            },
          }}
        >
          <NavRail
            expanded={expanded}
            mobile={isMobile}
            onToggleExpanded={toggleExpanded}
            onClose={() => setMobileOpen(false)}
            onOpenAccountMenu={setAccountAnchor}
            hiddenKeys={hiddenKeys}
          />
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {hidden ? (
          <MuiAppBar
            position="sticky"
            elevation={0}
            color="inherit"
            sx={{
              backgroundColor: '#fff',
              borderBottom: '1px solid #e6ebf0',
            }}
          >
            <Toolbar sx={{ minHeight: `${APP_HEADER_HEIGHT}px !important` }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  spacing={isMobile ? 0.25 : 2}
                  sx={{ minWidth: 0 }}
                >
                  <a
                    href={getEnv('MARKETING_URL')}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <img
                      src="/new_logo_light.png"
                      alt="Homepoint"
                      height={showTagline && isMobile ? 22 : 28}
                    />
                  </a>
                  {showTagline && (
                    <Typography
                      data-testid="public-header-tagline"
                      sx={{
                        color: '#52606d',
                        fontWeight: 500,
                        fontSize: isMobile ? 12 : 15,
                        lineHeight: 1.3,
                        whiteSpace: 'nowrap',
                        ...(!isMobile && {
                          pl: 2,
                          borderLeft: '1px solid #e6ebf0',
                        }),
                      }}
                    >
                      {BROWSE_TAGLINE}
                    </Typography>
                  )}
                </Stack>
                <Button
                  variant="contained"
                  size="small"
                  color="highlight"
                  sx={{ fontWeight: 600, height: 40, flexShrink: 0 }}
                  href={`${getEnv('MARKETING_URL')}/how-it-works`}
                >
                  How it works
                </Button>
              </Stack>
            </Toolbar>
          </MuiAppBar>
        ) : (
          <AppHeader
            mobile={isMobile}
            title={pageTitle}
            initials={initialsFor(currentUser ?? undefined)}
            onOpenMenu={() => setMobileOpen(true)}
            onOpenAccountMenu={setAccountAnchor}
          />
        )}
        {authLoading && <CircularProgress sx={{ m: 2 }} />}
        <Container
          maxWidth={fullBleedRoute ? false : 'lg'}
          data-testid="app-content"
          sx={{
            paddingTop: fullBleedRoute ? 0 : 3,
            pb: fullBleedRoute ? 0 : 2,
            ...(fullBleedRoute && { px: '0 !important' }),
          }}
        >
          <SuspenseOutlet backgroundColor="#f6f9fb" />
        </Container>
      </Box>

      <AccountMenu
        anchorEl={accountAnchor}
        onClose={() => setAccountAnchor(null)}
        name={fullName}
        email={currentUser?.email}
        widenLabel={widenLabel}
        onWiden={() => widenSignupIntentToBoth(currentUser)}
        onSettings={() => {
          setMobileOpen(false);
          navigate('/settings');
        }}
        onContact={() => {
          setMobileOpen(false);
          setContactDialogOpen(true);
        }}
        onLogout={() => {
          setMobileOpen(false);
          logout();
        }}
      />
      <ContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
      />
    </Box>
    </PageTitleContext.Provider>
  );
};

export default MiniDrawer;
